/**
 * Seat and license display helpers (EPIC-07).
 *
 * Seat numbers reach the portal from three independent places: the Stripe
 * subscription details payload (per-tier quantities), the company record
 * (prepaid seat blocks), and the graph's claimed-seat counts. Any of them can
 * be missing, a string, or, on legacy prepaid domains, out of step with each
 * other. Every count is normalized here so no surface renders undefined, NaN,
 * or a nonsensical "8 of 5 used".
 */

import { plural, t } from '@lingui/core/macro';

export type LicenseSplit = { pro: number; max: number };

/** One tier of a split that actually carries seats. */
export type LicenseSplitPart = { tier: 'pro' | 'max'; label: string; seats: number };

export type LicenseView = {
  /** Headline license count: the summed quantity across tiers. Always >= 1. */
  total: number;
  /** Normalized per-tier split, or null when no source carries one. */
  split: LicenseSplit | null;
  /** True when the split says more than the headline already does. */
  showSplit: boolean;
  /** Tiers holding at least one seat, Pro first. Empty when there is no split. */
  splitParts: LicenseSplitPart[];
};

/** A seat count as a non-negative integer, or null when the value is not one. */
export function seatCount(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/** Same as seatCount, but unusable values read as 0 instead of null. */
export function seatCountOrZero(value: unknown): number {
  return seatCount(value) ?? 0;
}

/**
 * Licenses shown on the Subscription page.
 *
 * Source order:
 * 1. `quantityPro` / `quantityMax` from GET /api/stripe/subscription/details,
 *    whose sum is the licensed total (the endpoint also sends that sum as
 *    `quantity`; the sum is recomputed here so a stale single-item quantity
 *    can never under-report the headline).
 * 2. For company subscriptions, the company's prepaid `licensedSeatsPro` /
 *    `licensedSeatsMax` block.
 * 3. The payload `quantity`, then the company's legacy `licensedSeats` total.
 */
export function licenseView(
  details: unknown,
  company: unknown,
  options: { allowCompanyFallback?: boolean } = {},
): LicenseView {
  const d = (details || {}) as Record<string, unknown>;
  const c = (company || {}) as Record<string, unknown>;

  const quantityPro = seatCount(d.quantityPro);
  const quantityMax = seatCount(d.quantityMax);

  let split: LicenseSplit | null =
    quantityPro !== null || quantityMax !== null
      ? { pro: quantityPro ?? 0, max: quantityMax ?? 0 }
      : null;

  if (!split && options.allowCompanyFallback) {
    const companyPro = seatCountOrZero(c.licensedSeatsPro);
    const companyMax = seatCountOrZero(c.licensedSeatsMax);
    if (companyPro + companyMax > 0) split = { pro: companyPro, max: companyMax };
  }

  const splitTotal = split ? split.pro + split.max : 0;
  const total = splitTotal > 0
    ? splitTotal
    : seatCount(d.quantity) || seatCount(c.licensedSeats) || 1;

  // A Pro-only split repeats the headline, so it stays hidden; any Max seat
  // makes the split the useful line.
  return { total, split, showSplit: !!split && split.max > 0, splitParts: splitParts(split) };
}

/**
 * The tiers of a split that actually hold seats, Pro first.
 *
 * A tier at zero says nothing worth a line of its own, so an all-Max account
 * reads "4 Max" instead of "0 Pro · 4 Max", and a blended one still reads
 * "3 Pro · 2 Max".
 */
export function splitParts(split: LicenseSplit | null): LicenseSplitPart[] {
  if (!split) return [];
  const parts: LicenseSplitPart[] = [];
  if (split.pro > 0) parts.push({ tier: 'pro', label: 'Pro', seats: split.pro });
  if (split.max > 0) parts.push({ tier: 'max', label: 'Max', seats: split.max });
  return parts;
}

/** Free seats in a prepaid tier block, never negative. */
export function freeSeats(licensedValue: unknown, claimedValue: unknown): number {
  return Math.max(seatCountOrZero(licensedValue) - seatCountOrZero(claimedValue), 0);
}

/**
 * Prepaid seat pill copy. Claimed counts can exceed the licensed block on
 * domains that claimed seats before the per-tier split existed, so the
 * over-claimed case is phrased as what it is instead of reading "8 of 5 used".
 * A tier the domain never bought says so, because "0 of 0 used" is accurate
 * and tells the admin nothing.
 */
export function seatUsageLabel(
  tierLabel: string,
  claimedValue: unknown,
  licensedValue: unknown,
): string {
  const claimed = seatCountOrZero(claimedValue);
  const licensed = seatCountOrZero(licensedValue);
  if (claimed > licensed) {
    const over = claimed - licensed;
    return t`${tierLabel} seats: ${claimed} in use, ${licensed} licensed (${over} over)`;
  }
  if (licensed === 0) {
    return t`${tierLabel} seats: none purchased`;
  }
  return t`${tierLabel} seats: ${claimed} of ${licensed} used`;
}

/**
 * Confirm copy for a prepaid tier move: no charge, but the target tier needs a
 * free seat. When the block is full (or over-claimed) the copy says the move
 * will be refused rather than promising a seat that does not exist.
 *
 * A domain that never bought the tier is the common shape, not the exception:
 * every Pro-only payment link leaves the Max block at zero. Saying "all 0 seats
 * are in use" there would be false twice over, so that case gets its own line.
 *
 * Sept 2026 pricing: Pro is an individual account (one seat, one person), so
 * "purchase more Pro seats" is never the fix. Only Max seats can be added for
 * a team, and the copy only ever suggests buying Max.
 */
export function prepaidTierMoveMessage(
  tierLabel: string,
  licensedValue: unknown,
  claimedValue: unknown,
): string {
  const licensed = seatCountOrZero(licensedValue);
  const claimed = seatCountOrZero(claimedValue);
  const free = freeSeats(licensedValue, claimedValue);
  const noSeatLine = tierLabel === 'Pro'
    ? t`Pro is an individual account (one seat, one person), so a team cannot add Pro seats; this move is refused.`
    : t`Purchase additional ${tierLabel} seats before moving this user; until then this move is refused.`;
  if (licensed === 0) {
    // es-CO carries three plural categories where English carries two, so the
    // seat count goes through the plural macro rather than an inline ternary.
    const overClaimed = claimed > 0
      ? ` ${plural(claimed, {
          one: `# ${tierLabel} seat is in use past the licensed block.`,
          other: `# ${tierLabel} seats are in use past the licensed block.`,
        })}`
      : '';
    return t`This domain has no prepaid ${tierLabel} seats.${overClaimed} ${noSeatLine}`;
  }
  if (free === 0) {
    return t`All ${licensed} prepaid ${tierLabel} seats are in use. ${noSeatLine}`;
  }
  return t`This moves the user to a prepaid ${tierLabel} seat. Your company has ${free} of ${licensed} ${tierLabel} seats free, so no charge applies. Continue?`;
}

/**
 * Admin-facing copy for a refused seat tier move.
 *
 * POST /api/company/users/set-tier answers with a code and an operator-facing
 * message. NO_SOURCE_SEAT's message names the Stripe subscription that needs
 * reconciling, which the company admin can neither act on nor should see, so
 * every recognized code gets copy written for the admin and only unrecognized
 * failures fall through to the API message. NO_SOURCE_SEAT is matched on the
 * code alone: an unexpected status is a poor reason to render a subscription
 * id into the page.
 */
export function tierMoveErrorMessage(error: unknown, tierLabel: string): string {
  const response = (error as {
    response?: { status?: number; data?: { code?: string; error?: string } };
  })?.response;
  const status = response?.status;
  const code = response?.data?.code;

  if (status === 409 && code === 'PRO_INDIVIDUAL_ONLY') {
    // Sept 2026 pricing: the server refuses a Pro seat on a multi-user domain.
    return t`Pro is an individual account. A domain with more than one user runs on Max for every user.`;
  }
  if (status === 409 && code === 'NO_SEATS_AVAILABLE') {
    return tierLabel === 'Pro'
      ? t`No Pro seats available. Pro is an individual account; team seats run on Max.`
      : t`No ${tierLabel} seats available. Purchase additional ${tierLabel} seats through your PrivacyPal contact.`;
  }
  if (code === 'NO_SOURCE_SEAT') {
    return t`This seat cannot be moved to ${tierLabel} automatically: the subscription has no seat to move from. Contact PrivacyPal support to reconcile the subscription.`;
  }
  if (status === 402 && code === 'PAYMENT_REQUIRED') {
    return t`Payment failed. Update the payment method on the Subscription page.`;
  }
  return response?.data?.error || t`Failed to move the user to ${tierLabel}`;
}
